import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Dropdown, Typography } from 'antd';
import moment from 'moment';
const { RangePicker } = DatePicker;
import * as styles from '@styles/calanderComponent.module.scss';
import { CalanderIconFilter } from '@src/assets/CalanderIconFilter';
const { Text } = Typography;

interface CustomDatePickerProps {
  onDateRangeChange: (
    dates: { startDate: string; endDate: string } | null
  ) => void;
  reset: boolean;
  setReset: (reset: boolean) => void;
  disabled?: boolean;
}

const CustomDatePicker = ({
  onDateRangeChange,
  reset,
  setReset,
  disabled,
}: CustomDatePickerProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedOption, setSelectedOption] = useState('Last 7 days');
  const [dateRange, setDateRange] = useState<any>([]);

  useEffect(() => {
    if (reset) {
      setSelectedOption('Last 7 days');
      setDateRange([]);
      setReset(false); // Reset the flag
    }
  }, [reset]);

  const formatDateToISO = (date: moment.Moment) => {
    return date.format('YYYY-MM-DDTHH:mm:ss.SSSSSS');
  };

  const handleMenuClick = (option: string) => {
    setSelectedOption(option);
    if (option === 'Custom') {
      setReset(true);
      setShowCalendar(true);
      setIsDropdownOpen(true);
    } else {
      setShowCalendar(false);
      let startDate: moment.Moment, endDate: moment.Moment;
      switch (option) {
        case 'Today':
          startDate = moment().startOf('day');
          endDate = moment().endOf('day');
          break;
        case 'Yesterday':
          startDate = moment().subtract(1, 'days').startOf('day');
          endDate = moment().subtract(1, 'days').endOf('day');
          break;
        case 'Last 7 days':
          startDate = moment().subtract(6, 'days').startOf('day');
          endDate = moment().endOf('day');
          break;
        case 'Last 10 days':
          startDate = moment().subtract(9, 'days').startOf('day');
          endDate = moment().endOf('day');
          break;
        case 'Last 30 days':
          startDate = moment().subtract(29, 'days').startOf('day');
          endDate = moment().endOf('day');
          break;
        case 'This month':
          startDate = moment().startOf('month');
          endDate = moment().endOf('month');
          break;
        default:
          startDate = moment().subtract(6, 'days').startOf('day');
          endDate = moment().endOf('day');
      }
      setDateRange([startDate, endDate]);
      onDateRangeChange({
        startDate: formatDateToISO(startDate),
        endDate: formatDateToISO(endDate),
      });
      setIsDropdownOpen(false);
    }
  };

  const items = [
    { label: 'Today', key: '1' },
    { label: 'Yesterday', key: '2' },
    { label: 'Last 7 days', key: '7' },
    { label: 'Last 10 days', key: '3' },
    { label: 'Last 30 days', key: '4' },
    { label: 'This month', key: '5' },
    { label: 'Custom', key: '6' },
  ];

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setDateRange(dates);
      onDateRangeChange({
        startDate: formatDateToISO(dates[0]),
        endDate: formatDateToISO(dates[1]),
      });
      setSelectedOption(
        `${dates[0].format('MMM D, YYYY')} - ${dates[1].format('MMM D, YYYY')}`
      );
    } else {
      onDateRangeChange(null);
    }
  };

  const menuProps = {
    items: items.map((item) => ({
      ...item,
      onClick: () => handleMenuClick(item.label),
    })),
  };

  return (
    <Dropdown
      menu={menuProps}
      trigger={['click']}
      open={isDropdownOpen}
      onOpenChange={(open) => {
        if (selectedOption !== 'Custom' || !open) {
          setIsDropdownOpen(open);
        }
      }}
    >
      <Button
        className={styles.mainButton}
        type="text"
        icon={
          <svg className={styles.filterColumnIcon}>
            <CalanderIconFilter />
          </svg>
        }
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        disabled={disabled}
      >
        <Text className={styles.filterText}>{selectedOption}</Text>
        {showCalendar && (
          <RangePicker
            className={styles.rangePicker}
            open={showCalendar}
            onChange={handleDateRangeChange}
            value={dateRange}
            onOpenChange={(open) => {
              setShowCalendar(open);
              if (!open && selectedOption === 'Custom') {
                setIsDropdownOpen(true);
              }
            }}
          />
        )}
      </Button>
    </Dropdown>
  );
};

export default CustomDatePicker;
